import React, { useState } from 'react';
import { observer } from 'mobx-react';
import {
  IconType, ButtonProps, Icon,
} from '@routific/hive';
import { CopyIcon } from '@routific/hive/v5';
import type { V5ButtonHierarchy } from '@routific/hive/v5';
import { useTranslation } from 'react-i18next';
// TODO: Remove moment and use Luxon instead when we move off of AntD
import moment, { Moment } from 'moment';
import { useStore } from '../../../../store/mst';
import ActionButton, { CommonActionTypes, ButtonTypes } from '../../../shared/ActionButton';
import { defaultDateFormat } from '../../../../constants';
import useDuplicateRoute from './hooks/useDuplicateRoute';
import useDuplicateRouteEligibility from './hooks/useDuplicateRouteEligibility';
import DuplicateDateModal from './DuplicateDateModal';

interface Props {
  routeUuids: string[]
  type?: ButtonTypes
  buttonProps?: ButtonProps
  disabled?: boolean
  // Render as a v5 Button, for the v5 chrome (the page footer's action band).
  v5?: boolean
  v5Hierarchy?: V5ButtonHierarchy
}

const DuplicateRouteButton: React.FC<Props> = ({
  routeUuids,
  type,
  buttonProps,
  disabled,
  v5,
  v5Hierarchy,
}) => {
  const store = useStore();
  const { t } = useTranslation('components');
  const selectedWorkspaceId = store.ui.selectedWorkspace?.id;

  // Business logic hooks
  const { isSubmitting, duplicateRoutes } = useDuplicateRoute();
  const { canDuplicate, totalOrdersCount, remainingOrders, isFreePlan } =
    useDuplicateRouteEligibility(routeUuids);

  // Local UI state
  const [visible, setVisible] = useState(false);
  const [selectedDate, selectDate] = useState<Moment | undefined>(
    store.ui.selectedDate ? moment(store.ui.selectedDate) : moment(),
  );

  const onSelectDate = (value: Moment) => {
    selectDate(value);
  };

  const handleCancel = () => {
    setVisible(false);
    selectDate(store.ui.selectedDate ? moment(store.ui.selectedDate) : moment());
  };

  const handleSubmit = async () => {
    if (!selectedDate || !selectedWorkspaceId || isSubmitting) {
      return;
    }

    await duplicateRoutes(routeUuids, selectedDate.format(defaultDateFormat));
    setVisible(false);
  };

  const handleClickDuplicate = (e?: any) => {
    e?.stopPropagation?.();
    if (!selectedWorkspaceId) return;
    setVisible(true);
  };

  const getTooltipText = () => {
    if (disabled) {
      return t('moreActionsButton.route.button.duplicate.tooltip.noPermission');
    }
    if (isFreePlan && !canDuplicate) {
      return t('moreActionsButton.route.button.duplicate.tooltip.freemiumLimitExceeded', {
        orderCount: totalOrdersCount,
        remainingOrders,
      });
    }
    if (type === ButtonTypes.BUTTON) {
      return t('moreActionsButton.route.button.duplicate.tooltip.button');
    }
    return undefined;
  };

  moment.updateLocale('en', {
    weekdaysMin: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
  });

  return (
    <div onClick={(e) => e.stopPropagation()}>
      <DuplicateDateModal
        visible={visible}
        selectedDate={selectedDate}
        isSubmitting={isSubmitting}
        routeCount={routeUuids.length}
        onSelectDate={onSelectDate}
        onSubmit={handleSubmit}
        onCancel={handleCancel}
      />
      <ActionButton
        v5={v5}
        v5Hierarchy={v5Hierarchy}
        v5Icon={<CopyIcon size="md" />}
        name={CommonActionTypes.DUPLICATE}
        type={type}
        disabled={disabled || (isFreePlan && !canDuplicate)}
        tooltipProps={{
          title: getTooltipText(),
        }}
        menuItemText={t('moreActionsButton.route.button.duplicate.text')}
        menuItemProps={{
          onClick: handleClickDuplicate,
          icon: <Icon icon={IconType.Copy} />,
        }}
        buttonProps={{
          icon: IconType.Copy,
          onClick: handleClickDuplicate,
          ...buttonProps,
        }}
      />
    </div>
  );
};

export default observer(DuplicateRouteButton);
